import React, { FC, useContext, useEffect, useRef, useState } from "react";
import { Badge, Button, Card, Col, Form, Image, Row, Table } from "react-bootstrap";
import { ImageUploadProvider, ImageUploadProviderContext } from "../../../providers/ImageUploadProvider";
import ImageUploader from "../../../components/ImageUploader";
import clsx from "clsx";
import { useAuth } from "../../../hooks/useAuth";
import moment from "moment";
import { useIntl } from "react-intl";
import { LANG_COMMON_CHOOSE, LANG_COMMON_DELETE_CONFIRM, LANG_COMMON_DELETE_ERROR, LANG_COMMON_DELETE_SUCCESS, LANG_COMMON_LOADING, LANG_COMMON_VIEW, LANG_PERSON_AGE, LANG_PERSON_EYES_COLOR, LANG_PERSON_HAIR_COLOR, LANG_PERSON_HAIR_TYPE, LANG_PERSON_HEIGHT, LANG_PERSON_RACE, LANG_PERSON_SKIN_COLOR } from "../../../lang";
import { OptionsContext } from "../../../providers/OptionsContextProvider";
import { PersonType } from "../../../types/PersonType";
import { useUtil } from "../../../hooks/useUtil";
//import "../treatment-table.css";
import { MedicalRecordEvolutionType, MedicalRecordType } from "../../../types/MedicalRecordType";
import { MedicalRecordModel } from "../../../models/MedicalRecordModel";
import { PersonModel } from "../../../models/PersonModel";
import { TreatmentMedicationType, TreatmentResultType, TreatmentType } from "../../../types/TreatmentType";
import CustomSelect from "../../../components/form/CustomSelect";
import TreatmentAddModal from "./modal/TreatmentAddModal";
import TreatmentResultAddModal from "./modal/TreatmentResultAddModal";
import { TreatmentModel } from "../../../models/TreatmentModel";
import { isResponseError, OptionType, ResponseErrorType } from "../../../types/CommonType";
import { TreatmentContext, TreatmentContextProvider } from "../../../providers/TreatmentContextProvider";
import Loader from "../../../components/messages/Loader";
import LoaderInside from "../../../components/messages/LoaderInside";
import Confirm from "../../../components/messages/Confirm";
import { toast } from "react-toastify";
import { FileUploadProvider, FileUploadProviderContext } from "../../../providers/FileUploadProvider";
import { ClipLoader } from "react-spinners";
import PDFModal from "../../../components/PDFModal";
import PrescriptionModal from "./modal/PrescriptionModal";

// GRID
import { ReactGrid, Column, Row as GridRow, TextCell, DateCell, DateCellTemplate, HeaderCell, Highlight, CellLocation } from "@silevis/reactgrid";

import './mre.scss';
import Error from "../../../components/messages/Error";
import { s } from "@fullcalendar/core/internal-common";


interface Props {
    active_treatment: TreatmentType | null
    medical_record: MedicalRecordType
}

const MedicalRecordTreatmentTab: FC<Props> = ({ active_treatment, medical_record }) => {
    const [showModal, setShowModal] = useState(false);
    const [showResultModal, setShowResultModal] = useState(false);
    const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
    const [treatments, setTreatments] = useState(medical_record.treatments);
    const [treatmentToEdit, setTreatmentToEdit] = useState<TreatmentType | null>(null);
    const [resultToEdit, setResultToEdit] = useState<TreatmentResultType | null>(null);
    const [selectedDate, setSelectedDate] = useState<string>(moment(new Date()).format("YYYY-MM-DD"))
    const [resultToDelete, setResultToDelete] = useState<TreatmentResultType | null>(null);
    const [focusedCell, setFocusedCell] = useState<CellLocation | null>(null);
    const [focusedValue, setFocusedValue] = useState<string | null | undefined>(null);
    const [savingResult, setSavingResult] = useState(false);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [showFullscreen, setShowFullscreen] = useState(false);


    const [highlights, setHighlights] = useState<Highlight[]>([]);

    const [printUrl, setPrintUrl] = useState("");
    const [showPrint, setShowPrint] = useState(false);

    const intl = useIntl();
    const { getOption } = useUtil();

    const calculateDaysOfStart = (treatment: TreatmentType, result: TreatmentResultType) => {
        let diff = (moment(result.date).diff(moment(treatment?.eoc_date, 'YYYY-MM-DD')) / (60 * 60 * 24 * 1000));

        return (diff + 1).toFixed(0);
    }

    const calculateDaysOfCycle = (treatment: TreatmentType, result: TreatmentResultType) => {
        let diff = (moment(result.date).diff(moment(treatment?.dum_date, 'YYYY-MM-DD')) / (60 * 60 * 24 * 1000));

        return (diff + 1).toFixed(0);
    }

    const selectResult = (date: string, results: TreatmentResultType[]) => {
        for (let i in results) {
            if (results[i].date === date) {
                setResultToEdit(results[i]);
                setSelectedDate(results[i].date);
                setShowResultModal(false);
                return;
            }
        }

        setResultToEdit(null);
        setSelectedDate(date);
        setShowResultModal(true);
    }

    const getColumns = (treatment: TreatmentType): Column[] => {
        const medications = TreatmentModel().groupMedications(treatment.medications || []).map((item) => ({
            columnId: `MED-${getOption(item.medication, TreatmentModel().getMedicationsOptions())?.value}`,
            width: 100
        }));

        return [
            { columnId: "date", width: 70 },
            { columnId: "days", width: 70 },
            ...medications,
            { columnId: "endometrium", width: 60 },
            { columnId: "right_ovary", width: 150 },
            { columnId: "left_ovary", width: 150 },
            { columnId: "total_gt_14", width: 75 },
            { columnId: "e2", width: 30 },
            { columnId: "lh", width: 30 },
            { columnId: "p4", width: 30 },
            { columnId: "others", width: 150 }
        ]
    };

    const getHighlights = (treatment: TreatmentType, rowId: number | string): Highlight[] => {
        const columns = getColumns(treatment);
        return columns.map((column) => {
            return {
                columnId: column.columnId,
                rowId: rowId,
                className: "highlighted"
            };
        });
    }

    const formatMedicationHeader = (label: string): string => {
        return label.length > 16 ? `${label.substring(0, 13)}...` : label;
    }

    const getHeaderRow = (treatment: TreatmentType): GridRow => {
        const medications = TreatmentModel().groupMedications(treatment.medications || []).map((item): HeaderCell => ({
            type: "header",
            text: formatMedicationHeader(getOption(item.medication, TreatmentModel().getMedicationsOptions().map((o) => { return { ...o, label: `${o.label} (${o.unit})` } }))?.label || "")
        }));

        return {
            rowId: "header",
            cells: [
                { type: "header", text: "DATA" },
                { type: "header", text: `D(ciclo/${TreatmentModel().isTreatmentTypeTE(treatment.type) ? "pe" : "eoc"})` },
                ...medications,
                { type: "header", text: "END(MM)" },
                { type: "header", text: "ÓVARIO DIR" },
                { type: "header", text: "ÓVARIO ESQ" },
                { type: "header", text: "TOTAL (>14)" },
                { type: "header", text: "E2" },
                { type: "header", text: "LH" },
                { type: "header", text: "P4" },
                { type: "header", text: "OUTROS EXAMES" }
            ]
        }
    }

    const getResults = (treatment: TreatmentType, result: TreatmentResultType): TextCell[] => {
        return TreatmentModel().groupMedications(treatment.medications || []).map((item, index) => {
            const value = (result.medications) ?
                `${TreatmentModel().getDailyDosage(
                    TreatmentModel().getMedicationMeta(item.medication, JSON.parse(result.medications)),
                    item.posology
                )}` : "";
            return {
                type: "text",
                text: value,
                nonEditable: value === "" ? true : false
            }
        });
    }

    const getRowHeight = (idx: number, result: TreatmentResultType): number => {
        const biggerText = Object.values(result).reduce((max, value) => {
            if (typeof value === 'string' && value.length > max.toString().length) {
                return value;
            }
            return max;
        }, "");

        return Math.max(20, (biggerText.toString().length / 25) * 20); // Adjust height based on text length
    };

    const getRows = (results: TreatmentResultType[], treatment: TreatmentType): GridRow[] => {


        return [
            getHeaderRow(treatment),
            ...results.map<GridRow>((result, idx) => ({
                rowId: idx,
                height: getRowHeight(idx, result),
                cells: [
                    { type: "date", date: moment(result.date).toDate(), format: Intl.DateTimeFormat(), nonEditable: true }, // Format in "Y-m-d"
                    { type: "text", text: `${calculateDaysOfCycle(treatment, result)}º / ${calculateDaysOfStart(treatment, result)}º`, nonEditable: true },
                    ...getResults(treatment, result),
                    { type: "text", text: result.endometrium || "" },
                    {
                        type: "text", text: result.right_ovary || ""
                    },
                    { type: "text", text: result.left_ovary || "" },
                    { type: "text", text: result.total_gt_14?.toString() || "" },
                    { type: "text", text: result.e2 || "" },
                    { type: "text", text: result.lh || "" },
                    { type: "text", text: result.p4 || "" },
                    { type: "text", text: result.others || "" }
                ]
            }))
        ]
    };


    // posição inicial (em px)
    const [pos, setPos] = useState({ bottom: 20, right: 20 });

    // usado para calcular deslocamento durante o drag
    const dragData = useRef<{ startX: number; startY: number; initBottom: number; initRight: number } | null>(null);

    // Handlers
    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();                   // evita seleção de texto
        dragData.current = {
            startX: e.clientX,
            startY: e.clientY,
            initBottom: pos.bottom,
            initRight: pos.right,
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!dragData.current) return;
        const dy = e.clientY - dragData.current.startY;
        const dx = e.clientX - dragData.current.startX;

        // No eixo Y, mover para cima ↓ aumenta bottom, mover para baixo ↑ diminui
        // No eixo X, mover para a esquerda ↑ aumenta right, mover para a direita ↓ diminui
        setPos({
            bottom: dragData.current.initBottom - dy,
            right: dragData.current.initRight - dx,
        });
    };

    const handleMouseUp = () => {
        dragData.current = null;
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
    };

    // Limpeza caso o componente seja desmontado durante um drag
    useEffect(() => {
        return () => handleMouseUp();
    }, []);

    return (
        <TreatmentContextProvider treatment={active_treatment}>
            <TreatmentContext.Consumer>
                {({ loadResults, loading, treatment, setTreatment, loadingResults, results, setResults }) => (
                    <>
                        {showModal && (
                            <TreatmentAddModal
                                show={showModal}
                                onHide={() => {
                                    setShowModal(false);
                                }}
                                onSave={(treatment) => {
                                    setTreatments([...treatments, treatment]);
                                    setTreatment(treatment);
                                    setShowModal(false);
                                }}
                                treatment={null}
                                medical_record={medical_record}
                            />
                        )}
                        {printUrl && showPrint && (
                            <PDFModal
                                url={printUrl}
                                show={true}
                                onHide={() => setShowPrint(false)}
                            />
                        )}
                        {treatmentToEdit && (
                            <TreatmentAddModal
                                show={true}
                                onHide={() => {
                                    setTreatmentToEdit(null);
                                }}
                                onSave={(treatment) => {
                                    let updatedTreatments = treatments;
                                    for (let i in treatments) {
                                        if (treatment.id === treatments[i].id) {
                                            updatedTreatments[i] = treatment;
                                            setTreatments(updatedTreatments);
                                        }
                                    }
                                    setTreatment(treatment);
                                    setTreatmentToEdit(null);
                                }}
                                treatment={treatmentToEdit}
                                medical_record={medical_record}
                            />
                        )}
                        {showPrescriptionModal && treatment && (
                            <PrescriptionModal
                                show={true}
                                onHide={() => {
                                    setShowPrescriptionModal(false);
                                }}
                                onSave={(treatment) => {
                                    setShowPrescriptionModal(false);
                                }}
                                treatment={treatment}
                                medical_record={medical_record}
                            />
                        )}
                        {treatment && showResultModal && (
                            <TreatmentResultAddModal
                                show={showResultModal}
                                onHide={() => setShowResultModal(false)}
                                onSave={(result) => {
                                    setShowResultModal(false);
                                    loadResults(treatment.medical_record_id, treatment.id);
                                }}
                                treatment={treatment}
                                onDateChange={(date) => selectResult(date, results)}
                                result={null}
                                key={`${'add'}`}
                                date={selectedDate}
                            />
                        )}
                        {treatment && resultToEdit && (
                            <TreatmentResultAddModal
                                show={true}
                                onHide={() => setResultToEdit(null)}
                                onSave={(result) => {
                                    setResultToEdit(null);
                                    loadResults(treatment.medical_record_id, treatment.id);
                                }}
                                treatment={treatment}
                                result={resultToEdit}
                                onDateChange={(date) => selectResult(date, results)}
                                key={`${resultToEdit.id}`}
                                date={resultToEdit.date}
                            />
                        )}
                        {resultToDelete && (
                            <Confirm
                                message={intl.formatMessage(LANG_COMMON_DELETE_CONFIRM)}
                                show={true}
                                onConfirm={async () => {
                                    setResultToDelete(null);
                                    const id = toast.loading(intl.formatMessage(LANG_COMMON_LOADING));
                                    let result = false;
                                    if (resultToDelete !== undefined && treatment) {
                                        result = await TreatmentModel().removeResult(treatment, resultToDelete);
                                    } else {
                                        return;
                                    }

                                    toast.dismiss(id);
                                    if (result) {
                                        toast.success(intl.formatMessage(LANG_COMMON_DELETE_SUCCESS));
                                        loadResults(treatment.medical_record_id, treatment.id);
                                    } else {
                                        toast.error(`${intl.formatMessage(LANG_COMMON_DELETE_ERROR)}!`, {
                                            hideProgressBar: true,
                                            autoClose: false
                                        });
                                    }
                                }}
                                onHide={() => setResultToDelete(null)}
                            />
                        )}

                        {errorMsg && (
                            <Error message={errorMsg} onHide={() => setErrorMsg(null)} show={true} />
                        )}

                        <div
                            style={{
                                'position': 'fixed',
                                'bottom': pos.bottom,
                                'right': pos.right,
                                'width': '400px',
                                'zIndex': 1054,
                                boxShadow: '0 4px 10px rgba(0,0,0,.15)',
                                background: '#fff',
                                transition: 'bottom 0.15s ease, right 0.15s ease',
                                userSelect: 'none',
                            }}>


                        </div>

                        <Row>
                            {treatment && (
                                <>
                                    <Col lg={{ span: 3 }} style={{ zIndex: 999 }}>
                                        <Form.Label>Tratamento</Form.Label>
                                        <CustomSelect
                                            name="treatment"
                                            key={`treatment-${treatment.id}-${treatment.type}`}
                                            onChange={(o: OptionType) => {
                                                for (let i in treatments) {
                                                    if (o.value === treatments[i].id) {
                                                        setTreatment(treatments[i]);
                                                    }
                                                }
                                            }}
                                            value={treatment.id}
                                            options={treatments.map((item) => {
                                                return {
                                                    'label': `${getOption(item.type, TreatmentModel().getTreatmentTypeOptions())?.label} - ${moment(item.eoc_date, 'YYYY-MM-DD').format('DD/MM/YYYY')}`,
                                                    'value': item.id
                                                };
                                            })}
                                            isClearable={false}
                                        />
                                    </Col>

                                    <Col lg={{ span: 5 }} className="mt-4">
                                        <a href="" className="btn btn-light" onClick={(e) => {
                                            e.preventDefault();
                                            setTreatmentToEdit(treatment);
                                        }}>
                                            <i className="bi bi-pencil-fill"></i> Editar
                                        </a>
                                        <FileUploadProvider url={undefined} onChange={() => { }} onDone={() => { }}>
                                            <FileUploadProviderContext.Consumer>
                                                {({ url, onChange, onRemove, loading, hasUploadError, hasDownloadError, print, uploadErrorMessage }) => (
                                                    <a href="" className="btn btn-light ms-2" onClick={(e) => {
                                                        e.preventDefault();
                                                        setShowPrint(false);
                                                        setPrintUrl("");
                                                        print(`/api/treatment/${treatment.id}`).then((resp) => {
                                                            if (typeof resp === 'string') {
                                                                setPrintUrl(resp);
                                                                setShowPrint(true);
                                                            }
                                                        });
                                                    }}>
                                                        {loading && (
                                                            <ClipLoader color={'#000'} loading={loading} size={'1rem'} />
                                                        )}
                                                        {!loading && (<><i className="bi bi-printer-fill"></i> Imprimir</>)}
                                                    </a>
                                                )}
                                            </FileUploadProviderContext.Consumer>
                                        </FileUploadProvider>
                                        <a href="" className="btn btn-light ms-2" onClick={(e) => {
                                            e.preventDefault();
                                            setShowPrescriptionModal(true);
                                        }}>
                                            {loading && (
                                                <ClipLoader color={'#000'} loading={loading} size={'1rem'} />
                                            )}
                                            {!loading && (<><i className="bi bi-file-medical-fill"></i> Receita</>)}
                                        </a>
                                        {/* <a href="" className="btn btn-light ms-2" onClick={(e) => {
                                            
                                        }}>
                                           <i className="bi bi-heart-pulse"></i> Gravidez
                                        </a>
                                        <a href="" className="btn btn-light ms-2" onClick={(e) => {
                                            
                                        }}>
                                           <i className="bi bi-person-check"></i> Nascido Vivo
                                        </a> */}
                                    </Col>
                                </>
                            )}
                            <Col lg={{ span: 2, offset: (treatment) ? 1 : 8 }}>
                                <Button
                                    className="btn-custom bg-custom-gradient btn-custom-small text-uppercase w-100 btn btn-primary mt-4"
                                    onClick={() => setShowModal(true)}
                                >NOVO TRATAMENTO</Button>
                            </Col>
                            <Col lg={12}>
                                <hr />
                            </Col>
                        </Row>
                        <Row>
                            <Col lg={12}>
                                {loadingResults && (
                                    <div className="loading-container">
                                        <LoaderInside />
                                    </div>
                                )}
                                {!loadingResults && treatment && treatment?.medications && (
                                    <>
                                        <Row style={{ 'position': 'relative' }} className={clsx({ 'treatment-fullscreen': showFullscreen })}>
                                            <Col lg={showFullscreen ? 10 : 9} style={{ 'paddingLeft': '0px' }}>
                                                <div className="treatment-grid-container" style={{ marginLeft: '.75rem', 'overflowX': 'scroll', 'position': 'relative', 'maxHeight': '600px' }}>
                                                    <ReactGrid
                                                        rows={getRows(results, treatment)}
                                                        columns={getColumns(treatment)}
                                                        onCellsChanged={(cells) => {
                                                            console.log("Cells changed:", cells);
                                                            cells.forEach((cell) => {
                                                                const rowId = cell.rowId;
                                                                const columnId = cell.columnId as string;
                                                                const newText = (cell.newCell as TextCell).text;

                                                                // Check if the columnId starts with "MED-"
                                                                if (columnId.startsWith("MED-")) {
                                                                    //@ts-ignore
                                                                    const medications = JSON.parse(results[rowId].medications || "{}").map((med: any) => {
                                                                        if (med.id === columnId.replace("MED-", "")) {
                                                                            return { ...med, value: newText };
                                                                        }
                                                                        return med;
                                                                    });
                                                                    //@ts-ignore
                                                                    results[rowId]['medications'] = JSON.stringify(medications);
                                                                } else {
                                                                    //@ts-ignore
                                                                    results[rowId][columnId] = newText;
                                                                }



                                                                // Commit to API
                                                                // @ts-ignore
                                                                const result = results[rowId];
                                                                const toastLoading = toast.loading("Atualizando resultado...");
                                                                TreatmentModel().updateResult({
                                                                    ...result
                                                                }, treatment).then(() => {
                                                                    toast.dismiss(toastLoading);
                                                                    toast.success("Resultado atualizado com sucesso!");
                                                                }).catch((error) => {
                                                                    toast.dismiss(toastLoading);
                                                                    toast.error("Erro ao atualizar resultado: " + error.message);
                                                                });
                                                            });
                                                            setResults([...results]);
                                                        }}
                                                        onFocusLocationChanged={(location) => {
                                                            const columnId = location.columnId as string;
                                                            //@ts-ignore
                                                            const cellValue = results[location.rowId][location.columnId];
                                                            setFocusedCell(location);

                                                            // Check if the columnId starts with "MED-"
                                                            if (columnId.startsWith("MED-")) {
                                                                //@ts-ignore
                                                                const medications = JSON.parse(results[location.rowId].medications || "{}").filter((med: any) => med.id === columnId.replace("MED-", ""));
                                                                //@ts-ignore
                                                                setFocusedValue(medications[0]?.value || "");
                                                            } else {
                                                                //@ts-ignore
                                                                setFocusedValue(cellValue);
                                                            }
                                                            setHighlights(getHighlights(treatment, location.rowId));
                                                        }}
                                                        stickyLeftColumns={2}
                                                        highlights={highlights}
                                                        initialFocusLocation={{ columnId: 'date', rowId: 0 }}
                                                        stickyTopRows={1}
                                                    />

                                                </div>
                                                <Button
                                                    className="btn-custom bg-custom-gradient btn-custom-small text-uppercase btn btn-primary my-3 ms-3"
                                                    disabled={savingResult}
                                                    onClick={async () => {
                                                        // Create or update
                                                        setSavingResult(true);
                                                        let resp: TreatmentResultType | ResponseErrorType =
                                                            await TreatmentModel().createResult(
                                                                {
                                                                    id: -1,
                                                                    treatment_id: treatment.id,
                                                                    date: moment(results[results.length - 1].date).add(1, 'day').format("YYYY-MM-DD"),
                                                                    medications: "[]",
                                                                }, treatment);
                                                        // Saving result
                                                        setSavingResult(false);
                                                        if (!isResponseError(resp)) {
                                                            const newResult = resp as TreatmentResultType;
                                                            const updatedResults = [...results, newResult];
                                                            setResults(updatedResults);
                                                            setFocusedCell({ columnId: 'date', rowId: updatedResults.length - 1 });
                                                        } else {
                                                            setErrorMsg((resp as ResponseErrorType).message);
                                                        }
                                                    }}
                                                >
                                                    <i className="bi bi-plus-circle-fill"></i> Adicionar Linha
                                                </Button>
                                                <Button
                                                    className="btn-custom bg-custom-gradient btn-custom-small text-uppercase btn btn-primary my-3 ms-3"
                                                    onClick={() => setShowFullscreen(!showFullscreen)}
                                                >
                                                    {showFullscreen ? (
                                                        <i className="bi bi-fullscreen-exit"></i>
                                                    ) : (
                                                        <i className="bi bi-arrows-fullscreen"></i>
                                                    )}
                                                </Button>
                                            </Col>
                                            <Col lg={showFullscreen ? 2 : 3}>
                                                <Card className="treatment-data-window">
                                                    <Card.Header
                                                    >
                                                        <Card.Title>Editar Dados</Card.Title>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <div className="row">
                                                            <Form.Group className="mb-3" controlId="formBasicEmail">
                                                                <Form.Control
                                                                    type="text"
                                                                    name="occupation"
                                                                    as="textarea"
                                                                    rows={6}
                                                                    value={focusedValue || ""}
                                                                    onChange={(e) => {
                                                                        setFocusedValue(e.target.value);
                                                                    }}
                                                                />
                                                            </Form.Group>
                                                        </div>
                                                    </Card.Body>
                                                    <Card.Footer>
                                                        <Row>
                                                            <Col lg={{ span: 6, offset: 6 }}>
                                                                <Button
                                                                    variant="primary"
                                                                    className="btn-custom bg-custom-gradient btn-custom-small text-uppercase w-100"
                                                                    onClick={() => {
                                                                        if (focusedCell && focusedValue !== null && treatment) {
                                                                            const rowId = focusedCell.rowId;
                                                                            const columnId = focusedCell.columnId as string;

                                                                            // Check if the columnId starts with "MED-"
                                                                            if (columnId.startsWith("MED-")) {
                                                                                //@ts-ignore
                                                                                const medications = JSON.parse(results[rowId].medications || "{}").map((med: any) => {
                                                                                    if (med.id === columnId.replace("MED-", "")) {
                                                                                        return { ...med, value: focusedValue };
                                                                                    }
                                                                                    return med;
                                                                                });
                                                                                //@ts-ignore
                                                                                results[rowId]['medications'] = JSON.stringify(medications);
                                                                            } else {
                                                                                //@ts-ignore
                                                                                results[rowId][columnId] = focusedValue;
                                                                            }

                                                                            // Commit to API
                                                                            const toastLoading = toast.loading("Atualizando resultado...");
                                                                            TreatmentModel().updateResult({
                                                                                //@ts-ignore
                                                                                ...results[rowId]
                                                                            }, treatment).then(() => {
                                                                                toast.dismiss(toastLoading);
                                                                                toast.success("Resultado atualizado com sucesso!");
                                                                                setResults([...results]);
                                                                            }).catch((error) => {
                                                                                toast.dismiss(toastLoading);
                                                                                toast.error("Erro ao atualizar resultado: " + error.message);
                                                                            });
                                                                        }
                                                                    }}
                                                                >
                                                                    Salvar
                                                                </Button>
                                                            </Col>
                                                        </Row>
                                                    </Card.Footer>
                                                </Card>
                                            </Col>
                                        </Row>



                                        {/* <Table>
                                            <thead>
                                                <tr>
                                                    <th>Data</th>
                                                    <th>Dia do Ciclo</th>
                                                    <th>Dias EOC</th>
                                                    {TreatmentModel().groupMedications(treatment?.medications).map((item, index) => (
                                                        <th>{getOption(item.medication, TreatmentModel().getMedicationsOptions().map((o) => { return { ...o, label: `${o.label} (${o.unit})` } }))?.label}</th>
                                                    ))}
                                                    <th>End (mm)</th>
                                                    <th>Óvario Direito</th>
                                                    <th>Óvario Esq</th>
                                                    <th>Total (&gt;14)</th>
                                                    <th>E2</th>
                                                    <th>LH</th>
                                                    <th>P4</th>
                                                    <th>Outros Exames</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {results.map((result, index) => (
                                                    <tr className={`${result.right_ovary !== null ? 'has-ultra' : ''}`}>
                                                        <td>
                                                            {moment(result.date).format("DD/MM/YYYY")}
                                                            {" "}
                                                            <a href="" onClick={(e) => {
                                                                e.preventDefault();
                                                                setResultToEdit(result);
                                                            }}>
                                                                <i className="bi bi-pencil-fill text-secondary"></i>
                                                            </a>
                                                            {" "}
                                                            <a href="" onClick={(e) => {
                                                                e.preventDefault();
                                                                setResultToDelete(result);
                                                            }}>
                                                                <i className="bi bi-trash-fill text-danger"></i>
                                                            </a>
                                                        </td>
                                                        <td>
                                                            {calculateDaysOfCycle(treatment, result)}°
                                                        </td>
                                                        <td>
                                                            {calculateDaysOfStart(treatment, result)}
                                                        </td>
                                                        {treatment?.medications && TreatmentModel().groupMedications(treatment?.medications).map((item, index) => (
                                                            <td>{(result.medications) ?
                                                                TreatmentModel().getDailyDosage(
                                                                    TreatmentModel().getMedicationMeta(item.medication, JSON.parse(result.medications)),
                                                                    item.posology
                                                                )
                                                                : ""}</td>
                                                        ))}
                                                        <td>{result.endometrium}</td>
                                                        <td>{result.right_ovary}</td>
                                                        <td>{result.left_ovary}</td>
                                                        <td>{result.total_gt_14}</td>
                                                        <td>{result.e2}</td>
                                                        <td>{result.lh}</td>
                                                        <td>{result.p4}</td>
                                                        <td>{result.others}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table> */}
                                    </>
                                )}
                            </Col>
                        </Row>
                    </>
                )}
            </TreatmentContext.Consumer>
        </TreatmentContextProvider>

    );
}

export default MedicalRecordTreatmentTab;